// src/features/owner/sections/system/LinkPreviewsSection.tsx — the link-preview
// kill switch + counters (1.24.00 F1). DEFAULT RUNNING.
//
// State (Running/Paused), who paused it and when, lifetime outcomes for every
// preview row ever written (ok / failed / blocked / internal), the last 24
// hours — and two controls: Pause, Resume. Pausing stops the platform from
// making outbound requests to third-party sites at all; it is the switch to
// reach for when a link fetch is hammering something, leaking a signal, or
// simply misbehaving faster than a fix can ship.
//
// Every write goes useOwnerAction → ownerService → POST /owner/actions/<slug>,
// each audited under its own slug; NEVER logAction (that is the client-only
// prototype log the Dashboard quick actions still use — the anti-pattern here).
// After each write the aggregate is refetched: the switch shows the server's
// truth, not an optimistic guess.
import { useState } from 'react';
import styled from 'styled-components';
import { useLinkPreviews } from '@/hooks/useLinkPreviews';
import { useOwnerAction } from '@/hooks/useOwnerAction';
import { Button, Badge } from '@/ui';
import { Icon } from '@/icons';
import { OwnerPanel, KpiCard, EmptyState } from '../../components';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

const Dl = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 6px 16px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  dt { color: ${({ theme }) => theme.color.textMuted}; }
  dd { margin: 0; color: ${({ theme }) => theme.color.textPrimary}; font-family: ${({ theme }) => theme.font.mono}; }
`;

const Note = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 70ch;
`;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function LinkPreviewsSection() {
  const { data, loading, error, refetch } = useLinkPreviews();
  const action = useOwnerAction();
  const [busy, setBusy] = useState<'pause' | 'resume' | null>(null);

  async function run(kind: 'pause' | 'resume') {
    setBusy(kind);
    const ok =
      kind === 'pause' ? await action.pauseLinkPreviews() : await action.resumeLinkPreviews();
    setBusy(null);
    if (ok) refetch();
  }

  // Guarded on `!data`, not on `loading` alone: refetch() after a write flips
  // loading back on, and blanking the whole panel for that beat would throw the
  // owner back to a spinner every time they touched the switch.
  if (loading && !data) return <p style={{ opacity: 0.6 }}>Loading…</p>;
  if (error || !data) return <EmptyState icon="link" message={error ?? 'No data'} />;

  const { paused, pausedAt, pausedBy, totals, recent } = data;

  return (
    <Stack>
      <OwnerPanel
        title="Link previews"
        action={
          <StatusRow>
            <Badge tone={paused ? 'amber' : 'cyan'}>{paused ? 'Paused' : 'Running'}</Badge>
          </StatusRow>
        }
      >
        <Stack>
          <Note>
            When someone posts a link, the server fetches the page at the other end and builds a
            preview card from it. Pausing stops that outbound fetch entirely. It is the switch to
            reach for when a fetch is hammering a site, or behaving badly faster than a fix can ship.
          </Note>
          <Note data-testid="link-previews-limits">
            Two honest limits before you flip it. A post made while previews are paused gets no
            card, and there is no re-fetch path: it never gets one later, not even after you
            resume. And editing a post&rsquo;s link while paused strips the card it already had:
            the old preview is cleared before the new fetch is enqueued, and the enqueue is the
            half the pause skips. Internal TruXSocial previews are unaffected either way: they
            are built from our own database and never touch the network.
          </Note>
          <Controls>
            {paused ? (
              <Button variant="primary" onClick={() => void run('resume')} disabled={busy !== null}>
                <Icon name="play" size={14} /> {busy === 'resume' ? 'Resuming…' : 'Resume'}
              </Button>
            ) : (
              <Button variant="danger" onClick={() => void run('pause')} disabled={busy !== null}>
                <Icon name="pause" size={14} /> {busy === 'pause' ? 'Pausing…' : 'Pause'}
              </Button>
            )}
            <Button variant="ghost" onClick={refetch} disabled={busy !== null}>
              <Icon name="refresh" size={14} /> Refresh
            </Button>
          </Controls>
          {/* Only under the paused state: the server sends both fields null while
              the fetcher is running, so rendering them unconditionally would show
              an em-dash pair that reads like missing data rather than "not paused". */}
          {paused && (
            <Dl data-testid="link-previews-pause-meta">
              <dt>Paused at</dt>
              <dd>{pausedAt ? formatWhen(pausedAt) : 'Not recorded'}</dd>
              <dt>Paused by</dt>
              <dd>{pausedBy || 'Not recorded'}</dd>
            </Dl>
          )}
        </Stack>
      </OwnerPanel>

      <KpiGrid>
        <KpiCard icon="check" tone="mint" label="Previews built (lifetime)" value={totals.ok} deltaPct={null} trend="flat" />
        <KpiCard icon="ban" tone="magenta" label="Failed (lifetime)" value={totals.failed} deltaPct={null} trend="flat" />
        <KpiCard icon="shield" tone="ember" label="Blocked (lifetime)" value={totals.blocked} deltaPct={null} trend="flat" />
        <KpiCard icon="link" tone="brand" label="Internal links (lifetime)" value={totals.internal} deltaPct={null} trend="flat" />
        <KpiCard icon="activity" tone="teal" label="Fetched (last 24h)" value={recent.fetched24h} deltaPct={null} trend="flat" />
        <KpiCard icon="hourglass" tone="ember" label="Failed (last 24h)" value={recent.failed24h} deltaPct={null} trend="flat" />
      </KpiGrid>
    </Stack>
  );
}
